import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import type { JSONSchema6TypeName } from "json-schema";
import { HackerModeIcon } from "@ui-icons";
import { type ExtendedJSONSchema, typeMap } from "../../SystemSettings.types";

interface SystemSettingFieldProps {
  fieldKey: string;
  options: ExtendedJSONSchema & { title?: string };
  isHidden: boolean;
  form: any;
}

const formatEnumOptionLabel = (
  value: string,
  enumLabels?: Record<string, string>
) => {
  if (enumLabels?.[value] != null) return enumLabels[value];
  if (value === "") return "Default";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export default function SystemSettingField({
  fieldKey,
  options,
  isHidden,
  form,
}: SystemSettingFieldProps) {
  const isBoolean = options.type === "boolean";
  const enumOptions = options.enum;
  const isEnumSelect = Array.isArray(enumOptions) && enumOptions.length > 0;
  const inputSetting = typeMap[options.type as JSONSchema6TypeName];
  const defaultValue = inputSetting?.defaultValue;

  return (
    <form.Field
      key={fieldKey}
      name={fieldKey}
      children={(field: any) => {
        const dirtySx = field.state.meta.isDirty
          ? {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary.main",
                borderWidth: 2,
              },
            }
          : undefined;

        const control = (() => {
          if (isBoolean) {
            return (
              <FormControlLabel
                control={<Switch size="small" />}
                label={options.title}
                checked={field.state.value ?? defaultValue}
                onChange={(_e: any, v: boolean) => {
                  field.handleChange(v);
                }}
                sx={{ m: 0 }}
              />
            );
          }
          if (isEnumSelect) {
            return (
              <TextField
                select
                fullWidth
                size="small"
                label={options.title}
                value={field.state.value ?? defaultValue ?? ""}
                onChange={(e) => field.handleChange(e.target.value)}
                slotProps={{
                  // "" is a real option; shrink label so it doesn't cover displayEmpty text
                  select: { displayEmpty: true },
                  inputLabel: { shrink: true },
                }}
                sx={dirtySx}
              >
                {enumOptions.map((opt) => (
                  <MenuItem
                    key={opt === "" ? "__default__" : String(opt)}
                    value={opt as string}
                  >
                    {formatEnumOptionLabel(String(opt), options.enum_labels)}
                  </MenuItem>
                ))}
              </TextField>
            );
          }
          const inputId = `system-settings-field-${fieldKey}`;
          return (
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel htmlFor={inputId}>{options.title}</InputLabel>
              <OutlinedInput
                id={inputId}
                type={inputSetting?.inputType}
                value={field.state.value ?? defaultValue}
                onChange={(e: any) => {
                  const value =
                    inputSetting?.inputType === "number"
                      ? e.target.valueAsNumber
                      : e.target.value;
                  field.handleChange(value);
                }}
                label={options.title}
                sx={dirtySx}
              />
            </FormControl>
          );
        })();

        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              {isHidden && (
                <HackerModeIcon sx={{ fontSize: "22px", verticalAlign: "middle" }} />
              )}
              <div className="flex items-center gap-2 flex-1">
                {control}
                {options.description && (
                  <Tooltip title={options.description} arrow placement="top">
                    <InfoOutlinedIcon
                      sx={{ fontSize: "18px", color: "grey.400", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
            {!field.state.meta.isValid && field.state.meta.isTouched && (
              <em role="alert" className="text-sm text-red-600">
                {field.state.meta.errors
                  .map((error: any) =>
                    typeof error === "string"
                      ? error
                      : error?.message || "Invalid value"
                  )
                  .join(", ")}
              </em>
            )}
          </div>
        );
      }}
    />
  );
}
